/**
 * Checks if a pathname matches the job hire route pattern. *
 * @param {string} pathname - The pathname to check.
 * @returns {boolean} - `true` if the pathname matches a job hire route; otherwise, `false`.
 */
export const isJobHireRoute = (pathname: string | null): boolean => {
  if (!pathname) return false;
  const jobHirePattern = /^\/(?:[a-z]{2}\/)?job\/[^/]+\/(applicants|candidates|viewers|users-who-favorite-my-job|flow)(\/.*)?$/;
  return jobHirePattern.test(pathname);
};

export const getJobSlugFromApplicantsFlow = (pathname: string | null): string | null => {
  if (!pathname) return null;

  // Matches:
  // /job/<slug>/applicants/flow
  // /en/job/<slug>/applicants/flow
  const match = pathname.match(/^\/(?:[a-z]{2}\/)?job\/([^/]+)\/applicants\/flow$/);

  return match ? match[1] : null;
};


/**
 * Checks if a pathname matches the job folder CV route pattern. *
 * @param {string} pathname - The pathname to check.
 * @returns {boolean} - `true` if the pathname matches a job folder CV route; otherwise, `false`.
 */
export const isJobFolderCvRoute = (pathname: string | null): boolean => {
  if (!pathname) return false;
  const jobFolderCvPattern = /^\/job\/[^/]+\/cv\/[^/]+$/;
  return jobFolderCvPattern.test(pathname);
};

/**
 * Checks if a pathname matches the landing matching route pattern. *
 * @param {string} pathname - The pathname to check.
 * @returns {boolean} - `true` if the pathname matches a landing matching route; otherwise, `false`.
 */
export const isLandingMatchingRoute = (pathname: string | null): boolean => {
  if (!pathname) return false;
  const landingMatchingPattern = /^\/landing\/matching(\/[a-f0-9-]+)?$/;
  return landingMatchingPattern.test(pathname);
};

/**
 * Checks if a pathname is included in the static routes. *
 * @param {string} pathname - The pathname to check.
 * @returns {boolean} - `true` if the pathname is included in any of the static routes; otherwise, `false`.
 */
export const isInStaticRoutesCheck = (pathname: string | null): boolean => {
  if (!pathname) return false;
  const staticRoutes = ['/cv/search', '/company/edit', '/offer/configurator', '/premium', 'landing/matching', '/job-add/edit', '/hire'];
  return staticRoutes.some((route) => pathname.includes(route));
};

/**
 * Checks if a route is a company wallet route.
 * @param {string | null} pathname - The pathname to check.
 * @param {string | null} searchParams - The URL search parameters string.
 * @returns {boolean} - `true` if the route is a company wallet route; otherwise, `false`.
 */
export const isCompanyWalletRoute = (pathname: string | null, searchParams: string | null): boolean => {
  if (!pathname) return false;
  const hasWalletPath = pathname.includes('/wallet');
  const isOrderPath = pathname.includes('/wallet/order');
  const hasCompanyRole = searchParams ? searchParams.includes('role=company') : false;
  return (hasWalletPath && hasCompanyRole) || isOrderPath;
};

/**
 * Determines whether a given pathname belongs to the "hire" section of the website.
 * The "hire" section contains routes related to company. *
 * @param {string | null} pathname - The full pathname to check.
 * @returns {boolean} - `true` if the pathname matches a valid "hire" route; otherwise, `false`.
 */
export const isHireRoute = (pathname: string | null): boolean => {
  if (!pathname) return false;
  return isInStaticRoutesCheck(pathname) || isJobHireRoute(pathname) || isJobFolderCvRoute(pathname) || isLandingMatchingRoute(pathname);
};
